/**
 * Length Switcher with Color Preservation
 *
 * Maintains color selection when switching between hair extension length products.
 * Integrates with Swatch King app for color swatches.
 */

class LengthSwitcher {
  constructor() {
    this.switcher = document.querySelector('.length-switcher');
    this.variantSelector = document.querySelector('[name="id"]');

    if (this.switcher) {
      this.init();
    }
  }

  init() {
    // Listen for length button clicks
    const lengthButtons = this.switcher.querySelectorAll('.length-switcher__option');

    lengthButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleLengthSwitch(button);
      });
    });

    // Restore color from URL parameter on page load
    this.restoreColorFromURL();
  }

  /**
   * Find the color selector element (Swatch King or standard select)
   */
  findColorSelector() {
    // Try to find Swatch King elements
    const swatchKingSelect = document.querySelector('select[data-option-name="Color"]') ||
                             document.querySelector('[name="options[Color]"]') ||
                             document.querySelector('select.single-option-selector[data-index="option1"]');

    if (swatchKingSelect) {
      return swatchKingSelect;
    }

    // Fallback to any select with color options
    const allSelects = document.querySelectorAll('select');
    for (let select of allSelects) {
      if (select.options && select.options.length > 0) {
        // Check if this looks like a color selector
        const firstOption = select.options[0].text || select.options[0].value;
        if (firstOption && firstOption.length > 0) {
          return select;
        }
      }
    }

    return null;
  }

  /**
   * Get the currently selected color
   */
  getCurrentColor() {
    // Method 1: Try Swatch King selected swatch
    const selectedSwatch = document.querySelector('.swatch-element.selected') ||
                          document.querySelector('.swatch.is-active') ||
                          document.querySelector('.swatch-element input:checked');

    if (selectedSwatch) {
      const colorValue = selectedSwatch.dataset?.value ||
                        selectedSwatch.dataset?.color ||
                        selectedSwatch.getAttribute('data-value') ||
                        selectedSwatch.querySelector('input')?.value;

      if (colorValue) {
        return colorValue;
      }
    }

    // Method 2: Try standard select dropdown
    const colorSelector = this.findColorSelector();
    if (colorSelector && colorSelector.value) {
      return colorSelector.value;
    }

    // Method 3: Try to find selected variant title
    const variantSelect = document.querySelector('[name="id"]');
    if (variantSelect && variantSelect.selectedOptions && variantSelect.selectedOptions[0]) {
      const selectedOption = variantSelect.selectedOptions[0];
      const variantTitle = selectedOption.dataset?.variantTitle || selectedOption.text;

      // Extract color from variant title (usually just the color name for single-option products)
      if (variantTitle) {
        return variantTitle.trim();
      }
    }

    return null;
  }

  /**
   * Handle length switch - navigate to new product with color parameter
   */
  handleLengthSwitch(button) {
    const targetURL = button.dataset.productUrl;
    const currentColor = this.getCurrentColor();

    // Build new URL with color parameter
    let newURL = targetURL;

    if (currentColor) {
      const separator = targetURL.includes('?') ? '&' : '?';
      newURL = `${targetURL}${separator}color=${encodeURIComponent(currentColor)}`;

      // Show loading state
      button.classList.add('loading');
    }

    // Navigate to new product with color preserved
    window.location.href = newURL;
  }

  /**
   * Restore color selection from URL parameter
   */
  restoreColorFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const colorParam = urlParams.get('color');

    if (!colorParam) return;

    // Wait for Swatch King and theme JS to initialize
    setTimeout(() => {
      this.selectColor(colorParam);

      // Clean up URL (remove color parameter)
      if (window.history && window.history.replaceState) {
        const cleanURL = window.location.pathname;
        window.history.replaceState({}, document.title, cleanURL);
      }
    }, 150);
  }

  /**
   * Select a color by value
   */
  selectColor(colorValue) {
    if (!colorValue) return;

    const normalizedColor = colorValue.toLowerCase().trim();

    // Method 1: Try clicking Swatch King swatch elements
    const swatchElements = document.querySelectorAll('.swatch-element');
    for (let swatch of swatchElements) {
      const swatchValue = swatch.dataset?.value ||
                         swatch.dataset?.color ||
                         swatch.querySelector('input')?.value;

      if (swatchValue && swatchValue.toLowerCase().trim() === normalizedColor) {
        const input = swatch.querySelector('input');
        const label = swatch.querySelector('label');

        if (input) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }

        if (label) {
          label.click();
        }

        this.showColorPreservedMessage(colorValue);
        return;
      }
    }

    // Method 2: Try standard select dropdown
    const colorSelector = this.findColorSelector();
    if (colorSelector && colorSelector.tagName === 'SELECT') {
      const options = Array.from(colorSelector.options);
      const matchingOption = options.find(
        opt => opt.value.toLowerCase().trim() === normalizedColor ||
               opt.text.toLowerCase().trim() === normalizedColor
      );

      if (matchingOption) {
        colorSelector.value = matchingOption.value;
        colorSelector.dispatchEvent(new Event('change', { bubbles: true }));
        this.showColorPreservedMessage(colorValue);
        return;
      }
    }

    // If we couldn't find exact match, try partial match
    this.selectColorFuzzy(normalizedColor);
  }

  /**
   * Fallback: Try fuzzy matching for color selection
   */
  selectColorFuzzy(colorValue) {
    const swatchElements = document.querySelectorAll('.swatch-element');

    for (let swatch of swatchElements) {
      const swatchValue = swatch.dataset?.value ||
                         swatch.dataset?.color ||
                         swatch.querySelector('input')?.value;

      if (swatchValue && swatchValue.toLowerCase().includes(colorValue)) {
        const label = swatch.querySelector('label');
        if (label) {
          label.click();
          this.showColorPreservedMessage(swatchValue);
          return;
        }
      }
    }
  }

  /**
   * Show notification that color was preserved
   */
  showColorPreservedMessage(colorName) {
    // Check if notification already exists
    if (document.querySelector('.color-preserved-notification')) return;

    const message = document.createElement('div');
    message.className = 'color-preserved-notification';
    message.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="10" fill="#10b981"/>
        <path d="M6 10l3 3 5-6" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <span>Color "${colorName}" selected</span>
    `;

    document.body.appendChild(message);

    // Trigger animation
    setTimeout(() => {
      message.classList.add('show');
    }, 10);

    // Remove after 2.5 seconds
    setTimeout(() => {
      message.classList.remove('show');
      setTimeout(() => message.remove(), 300);
    }, 2500);
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new LengthSwitcher();
  });
} else {
  new LengthSwitcher();
}
